'use client';

import { useState, useRef, useCallback, useMemo } from "react";
import Image from "@src/components/atoms/safe-image";
import { useSearchParams, useParams } from 'next/navigation';
import capitalizeFirstLetter from '@src/helper/capitalizeFirstLetter';

// Ctx
import useListListingCtx from "@src/context/list-listing-office-ctx";

// Assets
import Magnifying from "@public/png/magnifying-1.png"
import XIcon from "@public/svg/i-x.jsx";

// Components
import ModalSearch from "@src/components/atoms/modal-search-list-listing/modal-search-list-listing"
import ModalBantuanSearchListListing from "@src/components/atoms/modal-bantuan-search-list-listing/modal-bantuan-search-list-listing"
import ModalSaranLokasi from "@src/components/atoms/modal-saran-lokasi"
import ModalTanyaMarketing from "@src/components/atoms/modal-tanya-marketing"

// Hooks
import { useDebounce } from "@src/hooks/useDebounce";

// Api
import { useGetListLocation } from "@src/apis/list-listing/query";

const ListListingSearch = () => {
  const { handleApply, searchDisplay, setSearchDisplay } = useListListingCtx()

  const { city, area } = useParams()
  const searchParams = useSearchParams();

  const areaSlug = !!area && capitalizeFirstLetter(!!area ? area.split("-").join(" ") : "")
  const citySlug = !!city && capitalizeFirstLetter(!!city ? city.split("-").join(" ") : "")
  const cityName = !!area ? `${citySlug}, ${areaSlug}` : !!city ? `${citySlug}` : searchParams.get('cityName');

  const [searchModalDisplay, setSearchModalDisplay] = useState("") // this use for search
  const [isTyping, setIsTyping] = useState(false) // local loading state for immediate feedback
  const [params, setParams] = useState({
    q: !!cityName ? cityName : "",
    page: 1,
    _limit: 10
  })

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching
  } = useGetListLocation({ params })

  const listListing = useMemo(() => {
    if (!data?.pages?.length) return []

    return data.pages.map(d => d?.data?.data ?? []).flat()
  }, [data])

  const [openModal, setOpenModal] = useState({
    search: false,
    help: false,
    suggestLocation: false,
    tanyaMarketing: false,
  })

  const handleOpenModal = (type) => {
    const newObj = { ...openModal }

    for (const i in newObj) {
      newObj[i] = i === type
    }

    if (type === "search" && !!searchDisplay) {
      setSearchModalDisplay(searchDisplay)
    }

    setOpenModal(newObj)
  }

  const handleCloseModal = () => [
    handleOpenModal()
  ]

  const { debounceFn } = useDebounce({
    onChange: (newParams) => {
      setParams({ ...params, ...newParams })
      setIsTyping(false) // Stop typing state when API call starts
    }
  })

  const handleChangeSearch = (e) => {
    const newSearch = e.target.value ?? "";

    setSearchModalDisplay(newSearch)
    setIsTyping(true) // Set typing state immediately when user types
    debounceFn({ q: newSearch.trim(), page: 1 })
  }

  const observer = useRef();
  const lastItemRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleSelectLocation = (data) => {
    handleApply("search", data, handleCloseModal)
    setSearchDisplay(data.name)
    setSearchModalDisplay(data.name)
  }

  const handleClearSearch = (e) => {
    e.stopPropagation()
    setSearchDisplay("")
    setSearchModalDisplay("")
    handleApply("search", null)
  }

  return (
    <>
      <button onClick={() => handleOpenModal("search")} className={`h-[40px] flex justify-between cursor-pointer w-screen max-w-[421px] rounded ${searchDisplay ? "bg-[#fff]" : "bg-[#F6F6F6]"} p-[12px] flex items-center 
             dd2-xl:max-w-[unset] dd2-xl:w-full`}>
        <div className="flex items-center gap-2">
          <p className={`${searchDisplay ? "text-[#252526] font-satoshi-bold" : "text-[#808285]"} text-[17px] font-satoshi text-start truncate`}>{searchDisplay || "Masukkan kota/area"}</p>
          { !!searchDisplay && <button onClick={handleClearSearch} className="w-6 h-6 flex items-center justify-center rounded-[80px] bg-[#F5F5F5]"><XIcon /></button>}
        </div>
       
        <Image
          width="auto"
          height="auto"
          src={Magnifying}
          alt='Magnifying'
          className='ml-[12px] w-6 h-6 object-contain'
        />
      </button>

      <ModalSearch
        isOpen={openModal.search}
        onClose={handleCloseModal}
        onHelp={() => handleOpenModal("help")}
        onChange={handleChangeSearch}
        value={searchModalDisplay}
        lastItemRef={lastItemRef}
        data={listListing}
        isFetching={isFetching || isFetchingNextPage || isTyping}
        onSelect={handleSelectLocation}
      />
      <ModalBantuanSearchListListing
        isOpen={openModal.help}
        onClose={handleCloseModal}
        onSuggestLocation={() => handleOpenModal("suggestLocation")}
        onTanyaMarketing={() => handleOpenModal("tanyaMarketing")}
      />
      <ModalSaranLokasi
        isOpen={openModal.suggestLocation}
        onClose={handleCloseModal}
        onHelp={() => handleOpenModal("help")}
      />
      <ModalTanyaMarketing
        isOpen={openModal.tanyaMarketing}
        onClose={handleCloseModal}
        onHelp={() => handleOpenModal("help")}
      />
    </>
  )
}

export default ListListingSearch
